// src/polaris-components/SubscriptionDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { Invoice, getInvoices } from '../utils/subscriptionApi';
import { CustomerPortalButton } from './CustomerPortalButton';
import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import '../styles/subscription-dashboard.css';

export const SubscriptionDashboard: React.FC = () => {
  const {
    subscription,
    isLoading,
    error,
    refetch
  } = useSubscription();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (subscription) {
      fetchInvoices();
    }
  }, [subscription]);

  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const data = await getInvoices(5);
      setInvoices(data);
    } catch (err) {
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleCancelSuccess = () => {
    refetch();
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return 'N/A';
    }

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const formatPrice = (amount: number, currency?: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
      minimumFractionDigits: 2
    }).format(amount / 100);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      active: { label: 'Active', className: 'status-active' },
      trialing: { label: 'Trial', className: 'status-trial' },
      past_due: { label: 'Past Due', className: 'status-past-due' },
      canceled: { label: 'Canceled', className: 'status-canceled' },
      unpaid: { label: 'Unpaid', className: 'status-unpaid' },
      incomplete: { label: 'Incomplete', className: 'status-incomplete' }
    };
    const statusInfo = statusMap[status] || { label: status, className: 'status-default' };
    return <span className={`status-badge ${statusInfo.className}`}>{statusInfo.label}</span>;
  };

  if (isLoading) {
    return (
      <div className="subscription-dashboard">
        <div className="dashboard-loading">
          <div className="spinner"></div>
          <p>Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-dashboard">
        <div className="dashboard-error">
          <h2>⚠️ Error Loading Subscription</h2>
          <p>{error}</p>
          <button onClick={refetch} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="subscription-dashboard">
        <div className="no-subscription">
          <div className="empty-icon">📋</div>
          <h2>No Active Subscription</h2>
          <p>You don't have an active subscription yet. Choose a plan to get started!</p>
          <button
            onClick={() => navigate('/signup')}
            className="cta-btn"
            data-testid="view-plans-btn"
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-dashboard" data-testid="subscription-dashboard">
      <div className="dashboard-header">
        <h1>Billing</h1>
        <div className="header-actions">
          <CustomerPortalButton />
        </div>
      </div>

      {/* Current Plan Summary and Payment Method Grid */}
      <div className="billing-grid">
        {/* Current Subscription Card */}
        <div className="subscription-card current-plan-summary" data-testid="subscription-card">
          <h3 className="card-title">Current Plan Summary</h3>

          <div className="plan-summary-details">
            <div className="plan-detail-row">
              <span className="plan-detail-label">PLAN NAME</span>
              <span className="plan-detail-label">BILLING CYCLE</span>
              {subscription.planCategory !== 'organization' && (
                <span className="plan-detail-label">PLAN COST</span>
              )}
            </div>
            <div className="plan-detail-row values">
              <span className="plan-detail-value">
                {subscription.planCategory === 'organization' && subscription.organizationDisplayName
                  ? `${subscription.organizationDisplayName} Plan`
                  : subscription.planName}
              </span>
              <span className="plan-detail-value">
                {subscription.interval
                  ? subscription.interval.charAt(0).toUpperCase() + subscription.interval.slice(1)
                  : 'N/A'}
              </span>
              {subscription.planCategory !== 'organization' && (
                <span className="plan-detail-value">{formatPrice(subscription.amount, subscription.currency)}</span>
              )}
            </div>
          </div>
        </div>

        {/* Payment Method Card */}
        <div className="subscription-card payment-method-card">
          <h3 className="card-title">Payment Method</h3>
          <div className="payment-method-info">
            <div className="card-icon-placeholder">
              <div className="mastercard-icon"></div>
            </div>
            <div className="card-details">
              <div className="card-type">Master Card</div>
              <div className="card-number">**** **** **** 4002</div>
              <div className="card-expiry">Expiry on 20/2024</div>
              <div className="card-email">📧 billing@acme.corp</div>
            </div>
            <button className="change-btn">Change</button>
          </div>
        </div>
      </div>

      {/* Invoice Section */}
      <div className="payment-history-section">
        <h2>Invoice</h2>
        {invoicesLoading ? (
          <div className="section-loading">
            <div className="spinner-small"></div>
            <span>Loading invoices...</span>
          </div>
        ) : invoices.length === 0 ? (
          <div className="empty-state">
            <p>No invoices yet</p>
          </div>
        ) : (
          <div className="invoices-table">
            <div className="invoice-header">
              <span className="invoice-col">Invoice ID</span>
              <span className="invoice-col">Billing Date</span>
              <span className="invoice-col">Plan</span>
              <span className="invoice-col">Amount</span>
              <span className="invoice-col">Status</span>
            </div>
            {invoices.map((invoice) => (
              <div key={invoice.id} className="invoice-row" data-testid="invoice-item">
                <span className="invoice-col invoice-id">#{invoice.number || invoice.id.substring(3, 9)}</span>
                <span className="invoice-col">{formatDate(invoice.created)}</span>
                <span className="invoice-col">{subscription.planName}</span>
                <span className="invoice-col invoice-amount">{formatPrice(invoice.amountPaid, invoice.currency)}</span>
                <span className="invoice-col">
                  <span className={`invoice-status-badge status-${invoice.status}`}>
                    {invoice.status === 'paid' ? 'Paid' : invoice.status === 'open' ? 'Pending' : invoice.status}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Account Section */}
      <div className="danger-zone">
        <button
          onClick={() => setShowCancelModal(true)}
          className="delete-account-btn"
          data-testid="delete-account-btn"
        >
          DELETE ACCOUNT
        </button>
      </div>

      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onSuccess={handleCancelSuccess}
        currentPeriodEnd={subscription.currentPeriodEnd}
      />
    </div>
  );
};
